import { cn } from "@/design-system/lib/utils";

/**
 * A two-way switch for Edit wording ("Team default | My version", "Text |
 * Email"). The same shape as the design system's `ScopeToggle` — real buttons
 * in a labelled group, each carrying `aria-pressed`, the pressed one also the
 * bolder so the state is never colour alone — with caller-named options, since
 * neither pair here is "mine vs everyone's" in the order `ScopeToggle` draws.
 */
export interface WordingSwitchOption<T extends string> {
  value: T;
  label: string;
}

export function WordingSwitch<T extends string>({
  label,
  value,
  options,
  onChange,
  "data-testid": testId,
}: {
  label: string;
  value: T;
  options: readonly WordingSwitchOption<T>[];
  onChange: (next: T) => void;
  "data-testid"?: string;
}) {
  return (
    <div
      className="inline-flex overflow-hidden rounded-md border border-border-light"
      role="group"
      aria-label={label}
      data-testid={testId}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex items-center px-space-3 py-space-1 text-small",
              "transition-colors duration-fast ease-standard",
              "focus-visible:outline-none focus-visible:shadow-goldRing",
              active
                ? "bg-onyx font-semibold text-white-pure"
                : "bg-white-pure text-onyx hover:bg-white-warm",
            )}
            data-testid={testId ? `${testId}-${o.value}` : undefined}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
